<template>
  <div class="pa-4">
    <v-card outlined class="rounded-lg pa-6">
      <h3 class="mb-4">
        <v-icon right color="primary" class="ml-2">mdi-package-variant-closed</v-icon>
        هزینه بسته‌بندی
      </h3>
      
      <v-form ref="form" v-model="isValid">
        <v-radio-group v-model="packagingType" row class="mb-6">
          <v-radio value="fixed">
            <template v-slot:label>
              <v-icon right small class="ml-1">mdi-cash</v-icon>
              مبلغ ثابت
            </template>
          </v-radio>
          <v-radio value="percent">
            <template v-slot:label>
              <v-icon right small class="ml-1">mdi-percent</v-icon>
              درصد از قیمت
            </template>
          </v-radio>
          <v-radio value="tiered">
            <template v-slot:label>
              <v-icon right small class="ml-1">mdi-format-list-numbered</v-icon>
              تفکیک تعداد
            </template>
          </v-radio>
        </v-radio-group>

        <v-slide-y-transition mode="out-in">
          <!-- 1. مبلغ ثابت -->
          <div v-if="packagingType === 'fixed'" key="fixed">
            <v-row>
              <v-col cols="12" md="4">
                <PriceInput
                  v-model="fixedAmount"
                  label="مبلغ ثابت بسته‌بندی"
                  prepend-inner-icon="mdi-cash"
                  outlined
                  dense
                  class="rounded-lg"
                  hide-details="auto"
                  :rules="[v => !!v || 'وارد کردن مبلغ الزامی است']"
                />
              </v-col>
            </v-row>
          </div>

          <!-- 2. درصد از قیمت -->
          <div v-if="packagingType === 'percent'" key="percent">
            <v-row>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model.number="percentAmount"
                  label="درصد از قیمت"
                  outlined
                  dense
                  class="rounded-lg"
                  suffix="%"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  hide-details="auto"
                  :rules="[
                    v => (v !== null && v !== '') || 'وارد کردن درصد الزامی است',
                    v => (v >= 0 && v <= 100) || 'درصد باید بین ۰ تا ۱۰۰ باشد'
                  ]"
                ></v-text-field>
              </v-col>
            </v-row>
          </div>

          <!-- 3. تفکیک تعداد -->
          <div v-if="packagingType === 'tiered'" key="tiered">
            <div v-for="(item, index) in tieredCosts" :key="index" class="d-flex align-start mb-3 gap-3">
              <v-text-field
                v-model.number="item.quantity"
                label="تعداد"
                outlined
                dense
                class="rounded-lg flex-grow-1"
                type="number"
                min="1"
                hide-details="auto"
                :rules="[v => !!v || 'تعداد الزامی است']"
              ></v-text-field>
              
              <div class="flex-grow-1">
                <PriceInput
                  v-model="item.amount"
                  label="مبلغ بسته‌بندی"
                  prepend-inner-icon="mdi-cash"
                  outlined
                  dense
                  class="rounded-lg"
                  hide-details="auto"
                  :rules="[v => !!v || 'مبلغ الزامی است']"
                />
              </div>
              
              <!-- دکمه‌های هر سطر -->
              <div class="d-flex align-center mr-2 mt-1">
                <v-btn icon color="success" @click="addTier(index)" title="افزودن">
                  <v-icon>mdi-plus-circle-outline</v-icon>
                </v-btn>

                <v-btn 
                  icon 
                  color="error" 
                  @click="removeTier(index)" 
                  :disabled="tieredCosts.length === 1"
                  title="حذف"
                >
                  <v-icon>mdi-trash-can-outline</v-icon>
                </v-btn>
              </div>
            </div>
          </div>
        </v-slide-y-transition>
      </v-form>

      <v-btn class="btn-get-code rounded-lg px-6 mt-8" @click="saveSettings">
        <v-icon left>mdi-content-save</v-icon>
        ذخیره تنظیمات
      </v-btn>
    </v-card>
  </div>
</template>

<script>
// مسیر کامپوننت PriceInput را در صورت نیاز اصلاح کنید
import PriceInput from "../../../../core/plugins/TitleRibbon/pages/pickers/priceInput.vue";

export default {
  name: "PackagingCostTab",
  components: {
    PriceInput
  },
  data: () => ({
    isValid: false,
    packagingType: "fixed", // 'fixed', 'percent', 'tiered'
    fixedAmount: "",
    percentAmount: "",
    tieredCosts: [
      { quantity: null, amount: "" } // ردیف پیش‌فرض
    ]
  }),
  methods: {
    addTier(index) {
      // اضافه کردن یک ردیف جدید دقیقاً بعد از ایندکس فعلی
      this.tieredCosts.splice(index + 1, 0, { quantity: null, amount: "" });
    },
    removeTier(index) {
      // جلوگیری از حذف شدن آخرین ردیف باقی‌مانده
      if (this.tieredCosts.length > 1) {
        this.tieredCosts.splice(index, 1);
      }
    },
    saveSettings() {
      // بررسی اعتبارسنجی فرم
      if (!this.$refs.form.validate()) {
        this.$toast.error("لطفاً مقادیر خواسته‌شده را به‌درستی وارد کنید.");
        return;
      }

      // تبدیل مقادیر فرمت‌شده PriceInput (حذف کاماها در صورت وجود)
      const cleanNumber = (val) => (val ? Number(String(val).replace(/,/g, "")) : 0);

      const payload = {
        type: this.packagingType,
        fixed: cleanNumber(this.fixedAmount),
        percent: Number(this.percentAmount),
        tiered: this.tieredCosts.map(item => ({
          quantity: Number(item.quantity),
          amount: cleanNumber(item.amount)
        }))
      };

      console.log("تنظیمات بسته‌بندی:", payload);
      this.$toast.success("تنظیمات با موفقیت ذخیره شد");
    }
  }
};
</script>

<style scoped>
.gap-3 { gap: 12px; }
</style>
